import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import useAllProfessionals from "@/features/professionals/hooks/use-all-professional";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { EventType } from "@/features/supply-config/event-types-api";

// One selectable individual professional, keyed by the EventType that carries
// its scheduling URL. Professional display fields are joined from the full
// professionals list so we get picture/type/experience (the event-type include
// only carries {id, name}).
export interface IndividualOption {
  eventTypeId: string;
  prof: {
    id: string;
    name: string;
    picture: string | null;
    type: string | null;
    specialization: string | null;
    years_of_experience: number | null;
  };
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function subtitle(p: IndividualOption["prof"]): string {
  const parts: string[] = [];
  if (p.type) parts.push(p.type);
  if (p.specialization) parts.push(p.specialization);
  if (typeof p.years_of_experience === "number") {
    parts.push(
      `${p.years_of_experience} yr${p.years_of_experience === 1 ? "" : "s"}`,
    );
  }
  return parts.join(" · ");
}

function ProfCard({
  prof,
  size = "md",
}: {
  prof: IndividualOption["prof"] | null;
  size?: "sm" | "md";
}) {
  if (!prof) {
    return <span className="text-sm text-gray-400">Select a professional</span>;
  }
  const sub = subtitle(prof);
  const avatarSize = size === "sm" ? "h-8 w-8" : "h-10 w-10";
  return (
    <div className="flex items-center gap-3 min-w-0">
      <Avatar className={avatarSize}>
        {prof.picture ? <AvatarImage src={prof.picture} alt={prof.name} /> : null}
        <AvatarFallback className="text-xs">{initials(prof.name)}</AvatarFallback>
      </Avatar>
      <div className="flex flex-col min-w-0 text-left">
        <span className="text-sm text-gray-900 truncate">{prof.name}</span>
        {sub && <span className="text-xs text-gray-500 truncate">{sub}</span>}
      </div>
    </div>
  );
}

interface Props {
  // Individual event types scoped to the bundle's managed events.
  eventTypes: EventType[];
  value: string | null; // selected eventTypeId
  onChange: (eventTypeId: string) => void;
}

export default function DecoratorProfessionalPicker({
  eventTypes,
  value,
  onChange,
}: Props) {
  const { data: profData } = useAllProfessionals();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // Join each individual event type to full professional data for rich display.
  const options: IndividualOption[] = useMemo(() => {
    const byId = new Map<string, any>(
      (profData?.professionals ?? []).map((p: any) => [p.id, p]),
    );
    return eventTypes
      .filter((et) => !!et.professional_id && !!et.scheduling_url)
      .map((et) => {
        const full = byId.get(et.professional_id as string);
        return {
          eventTypeId: et.id,
          prof: {
            id: et.professional_id as string,
            name: full?.name ?? et.professional?.name ?? "Unknown",
            picture: full?.picture ?? null,
            type: full?.type ?? null,
            specialization: full?.specialization ?? null,
            years_of_experience: full?.years_of_experience ?? null,
          },
        };
      });
  }, [eventTypes, profData?.professionals]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(({ prof }) => {
      if (prof.name.toLowerCase().includes(q)) return true;
      if (prof.specialization?.toLowerCase().includes(q)) return true;
      if (prof.type?.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [options, query]);

  const selected = useMemo(
    () => options.find((o) => o.eventTypeId === value) ?? null,
    [options, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-auto py-2"
        >
          <ProfCard prof={selected?.prof ?? null} />
          <ChevronsUpDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            autoFocus
            placeholder="Filter by name, type, or specialization…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="max-h-72 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="p-3 text-sm text-gray-500">No professionals found.</p>
          ) : (
            filtered.map((o) => (
              <button
                key={o.eventTypeId}
                type="button"
                onClick={() => {
                  onChange(o.eventTypeId);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between gap-2 p-2 hover:bg-gray-50 text-left"
              >
                <ProfCard prof={o.prof} size="sm" />
                {value === o.eventTypeId && (
                  <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                )}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
